import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getActiveSubscribeCampaign } from '../../config/campaigns';
import {
  DEFAULT_THX_HEADER,
  DEFAULT_THX_MESSAGE,
  DEFAULT_THX_MESSAGE_SUFFIX,
  DEFAULT_BACKGROUND_IMAGE,
  DEFAULT_MOBILE_IMAGE,
} from '../../config/defaults';
import { useSyneriseTracking } from '../shared/useSyneriseTracking';
import { pushSubscribeSuccessEvent } from '../shared/GtmDataLayerPush';
import { submitSyneriseFallback } from '../../api/subscribeApi';
import Header from '../shared/Header';
import NotFoundPage from '../shared/NotFoundPage';
import type { SubscribeSuccessState } from './types';
import './SubscribeThankYouPage.scss';

/**
 * Real /:slug/dziekujemy route — mirrors subscribe_base.html.twig's typ.html.twig exactly
 * (header + hero image + optional diagonal decor stay in place, same as the form page), not a
 * modal. Reached via navigate(..., {state}) from useSubscribeSubmit.ts; a direct visit/refresh
 * (no state) still renders the same page, just without personalization/tracking.
 */
export default function SubscribeThankYouPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const config = getActiveSubscribeCampaign(slug);
  const location = useLocation();
  const state = location.state as SubscribeSuccessState | undefined;

  useEffect(() => {
    if (state) pushSubscribeSuccessEvent(slug, state.leadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, !!state]);

  useSyneriseTracking(state, () => {
    if (state) {
      submitSyneriseFallback({ slug, ...state }).catch(() => {
        /* best-effort, mirrors legacy behavior of swallowing Synerise failures */
      });
    }
  });

  if (!config) {
    return <NotFoundPage />;
  }

  // thxMessage === '' (explicitly empty, not just unset) means "no subtitle at all" — e.g. a
  // campaign with no email step has nothing sensible to say in place of DEFAULT_THX_MESSAGE's
  // "we sent you a confirmation email" text. null/undefined still falls back to the default,
  // unchanged for every campaign that doesn't set thxMessage at all.
  const message = (
    <div className="form-copy">
      <h1>{config.thxHeader || DEFAULT_THX_HEADER}</h1>
      {config.thxMessage !== '' && (
        <p>
          {config.thxMessage || DEFAULT_THX_MESSAGE}
          {!config.thxMessage && ` ${DEFAULT_THX_MESSAGE_SUFFIX}`}
        </p>
      )}
    </div>
  );

  // TY page shows the campaign's own hero graphic when it sets one, else the shared default —
  // existing campaigns' backgroundImage/mobileImage already equal these defaults (unified visual
  // per earlier client request), so this is a no-op for them; a campaign with its own dedicated
  // image (e.g. octavia30lat) now gets it on the TY page too, not just the form.
  const images = (
    <>
      <img src={config.backgroundImage || DEFAULT_BACKGROUND_IMAGE} className="background-image full-height cover" alt="" />
      <img src={config.mobileImage || DEFAULT_MOBILE_IMAGE} className="background-image-mobile full-height cover" alt="" />
    </>
  );

  if (config.template === 'form-old-layout') {
    return (
      <>
        <Header />
        <div className="main-container">
          <div className="form-outer-container form-outer-container--message">
            <div className="form-bg">
              <div className="form-container">
                <div className="form-row">{message}</div>
              </div>
            </div>
          </div>
          {images}
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-container row-layout-template thx-page">
        <div className="form-outer-container form-outer-container--message">
          <div className="form-bg">
            <div className="form-container">
              <div className="form-triangle-decor" />
              <div className="form-row">{message}</div>
            </div>
          </div>
        </div>
        <div className="main-image main-image--thx">{images}</div>
      </div>
    </>
  );
}
